<!--PLACEHOLDER {build} add more info pertaining to manufacturing-->
<!--    ~ General: number of parts, material volume (total and per part)-->
<!--    ~ Manufacturing: cutting method, cutting time?, cutter data, raw material cost, ???-->
<!--    ~ 3D Printing: estimated time, raw material cost, ???-->
<template>
    <report-tab :name="name" title="Manufacturing Report" :icon="$icons.reportGears"
                :loading="loading" element-loading-text="Generating report"
                :url-help="$urls.HELP_REPORTS" :click-x="clickX">
        <h3>Table of Contents</h3>
        <ol>
            <li class="toc-bullet">
                <el-link type="primary" href="#gear-parts">Gears</el-link>
                <ol v-for="(part, i) in gearParts" :key="i">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="part.href">{{ part.label }}</el-link>
                    </li>
                </ol>
            </li>
        </ol>
        <hr>
        <div id="gear-parts" v-show="!loading">
            <span v-if="gearParts.length === 0" class="no-data">
                No gears in model.
            </span>
            <div v-else v-for="(part, i) in gearParts" :key="i">
                <h3 :id="part.id">
                    {{ part.label }}
                    &nbsp;&nbsp;&nbsp;
                    <el-button class="collapse-btn" @click="collapsePart(part)" icon="el-icon-minus" type="text"/>
                    <el-button class="collapse-btn" @click="expandPart(part)" icon="el-icon-plus" type="text"/>
                </h3>
                <el-collapse class="collapse-section" v-model="activePartSections" @change="changeCollapse">
                    <el-collapse-item v-if="part.table.tool" :name="collapseName('Tooling', part)">
                        <span slot="title" class="collapse-title">Tooling</span>
                        <report-table-params :table="part.table.tool"/>
                    </el-collapse-item>
                    <el-collapse-item :name="collapseName('Material', part)">
                        <span slot="title" class="collapse-title">Material</span>
                        <report-table-params :sub-title="$handlers.gear.MATERIAL_LABEL.agma2001D04" :table="part.table.agma2001D04">
                            <div slot="empty" class="empty-notes">
                                <span>AGMA material specification is undefined.</span>
                            </div>
                        </report-table-params>
                        <report-table-params :sub-title="$handlers.gear.MATERIAL_LABEL.iso633652003" :table="part.table.iso633652003">
                            <div slot="empty" class="empty-notes">
                                <span>ISO material specification is undefined.</span>
                            </div>
                        </report-table-params>
                    </el-collapse-item>
                    <el-collapse-item :name="collapseName('Flank Quality', part)">
                        <span slot="title" class="collapse-title">Flank Quality</span>
                        <report-table-params :sub-title="$handlers.gear.QUALITY_LABEL.agma13281B14" :table="part.table.agma13281B14">
                            <div slot="empty" class="empty-notes">
                                <span>AGMA flank tolerances are undefined.</span>
                            </div>
                        </report-table-params>
                        <report-table-params :sub-title="$handlers.gear.QUALITY_LABEL.iso132812013" :table="part.table.iso132812013">
                            <div slot="empty" class="empty-notes">
                                <span>ISO flank tolerances are undefined.</span>
                            </div>
                        </report-table-params>
                    </el-collapse-item>
                    <el-collapse-item :name="collapseName('Radial Quality', part)">
                        <span slot="title" class="collapse-title">Radial Quality</span>
                        <report-table-params :sub-title="$handlers.gear.QUALITY_LABEL.agma20152B15" :table="part.table.agma20152B15">
                            <div slot="empty" class="empty-notes">
                                <span>AGMA radial tolerances are undefined.</span>
                            </div>
                        </report-table-params>
                        <report-table-params :sub-title="$handlers.gear.QUALITY_LABEL.iso132822020" :table="part.table.iso132822020">
                            <div slot="empty" class="empty-notes">
                                <span>ISO radial tolerances are undefined.</span>
                            </div>
                        </report-table-params>
                    </el-collapse-item>
                </el-collapse>
            </div>
        </div>
    </report-tab>
</template>

<script>
  import ReportTab from "./ReportTab";
  import ReportTableParams from "./ReportTableParams";
  import ReportPart from "./ReportPart";
  import LogTable from "./LogTable";
  import {copyDeep, sortByString} from "../../assets/js/utils";
  import {getObjectById} from "../../store/helpers";
  import {apolloQuery} from "../../config/apollo";
  import gql from "graphql-tag";

  export default {
    name: "ReportMfg",
    components: {ReportTab, ReportPart, LogTable, ReportTableParams},
    props: {
      name: String,  // unique tab name
      clickX: Function,
    },

    units: {
      angle: 'deg',
    },
    collapseNames: {
      part: ['Tooling', 'Material', 'Flank Quality', 'Radial Quality'],
    },

    data() {
      return {
        // APOLLO
        gearParts: [],
        loading: true,

        // COLLAPSIBLE SECTIONS
        activePartSections: [],
      }
    },

    mounted() {
      // Called every time the report is opened.

      // map part data to local state
      this.mapParts();

      this.$nextTick(() => {
        this.loading = false;
        window.MathJax.typeset();
      });
    },
    methods: {
      // APOLLO
      async queryMaterials(part) {
        // API query of AGMA & ISO materials, if specified.

        const gear = part.obj;
        if (!gear.material) return;

        // initialize
        let args = '';
        let variables = {};

        // AGMA query options
        let queryMaterialAgma = '';
        if (gear.material.agma2001D04) {
          args += '$agmaId: Int!, ';
          variables.agmaId = gear.material.agma2001D04.Id;
          queryMaterialAgma = `materialAgma2001D04 (Id: $agmaId) {
              ${this.$requests.eng.MaterialDataAGMA2001_D04}
            }`;
        }

        // ISO query options
        let queryMaterialIso = '';
        if (gear.material.iso633652003) {
          args += '$isoId: Int!';
          variables.isoId = gear.material.iso633652003.Id;
          queryMaterialIso = `materialIso633652003 (Id: $isoId) {
              ${this.$requests.eng.MaterialDataISO6336_5_2003}
            }`;
        }

        // API query
        const query = {
          fetchPolicy: "cache-first",
          variables,
          query: gql`query getMaterials (${args}) {
            ${queryMaterialAgma}
            ${queryMaterialIso}
          }`,
        };
        const {data: {materialAgma2001D04, materialIso633652003}} = await apolloQuery(query);

        // mutate local state; updates report tables
        part.table.agma2001D04 = this.$handlers.gear.tableAgma2001D04({...materialAgma2001D04, ...gear.material.agma2001D04});
        part.table.iso633652003 = this.$handlers.gear.tableIso633652003({...materialIso633652003, ...gear.material.iso633652003});
      },
      async queryFlankQualities(part) {
        // API query of AGMA & ISO flank tolerances, if specified.

        const query = {
          fetchPolicy: "cache-first",
          variables: {Id: part.obj.Id},
          query: gql`query getFlankTolerances ($Id: ID!) {
            gearDetail (Id: $Id) {
                Id
                quality {
                    agma13281B14 {
                        ${this.$requests.eng.QualityDataAGMA1328_1_B14}
                    }
                    iso132812013 {
                        ${this.$requests.eng.QualityDataISO1328_1_2013}
                    }
                }
            }
          }`,
        };
        const {data: {gearDetail: {quality}}} = await apolloQuery(query);

        // mutate local state; updates report tables
        part.table.agma13281B14 = this.$handlers.gear.tableAgma13281B14(quality ? quality.agma13281B14 : null);
        part.table.iso132812013 = this.$handlers.gear.tableIso132812013(quality ? quality.iso132812013 : null);
      },
      async queryRadialQualities(part) {
        // API query of AGMA & ISO radial tolerances, if specified.

        const query = {
          fetchPolicy: "cache-first",
          variables: {Id: part.obj.Id},
          query: gql`query getRadialTolerances ($Id: ID!) {
            gearDetail (Id: $Id) {
                Id
                quality {
                    agma20152B15 {
                        ${this.$requests.eng.QualityDataAGMA2015_2_B15}
                    }
                    iso132822020 {
                        ${this.$requests.eng.QualityDataISO1328_2_2020}
                    }
                }
            }
          }`,
        };
        const {data: {gearDetail: {quality}}} = await apolloQuery(query);

        // mutate local state; updates report tables
        part.table.agma20152B15 = this.$handlers.gear.tableAgma20152B15(quality ? quality.agma20152B15 : null);
        part.table.iso132822020 = this.$handlers.gear.tableIso132822020(quality ? quality.iso132822020 : null);
      },
      partFields(part) {
        // Helper method to map fields used by all part mappings.

        return {
          Id: part.Id,  // collapse naming
          obj: part.obj,  // required for queries of material and quality data
          id: this.$handlers.model.idPart(part),  // href target
          href: this.$handlers.model.hrefPart(part),  // href source
          label: this.$handlers.model.partLabel(part),  // TOC and sorting
        }
      },
      mapGearDetailPart(part) {
        // Map detailed gear part to local state.

        // deep copy gear object; avoids mutating Apollo cache with units conversion
        const gear = copyDeep(part.obj);

        // convert units
        this.$handlers.gear.convertGearGeom(gear, undefined, this.$options.units);

        return {
          ...this.partFields(part),
          table: {
            tool: this.$handlers.gear.tableGearTool(gear, this.$options.units),
            // materials are queried on expand of collapsible
            agma2001D04: [],
            iso633652003: [],
            // flank qualities are queried on expand of collapsible
            agma13281B14: [],
            iso132812013: [],
            // radial qualities are queried on expand of collapsible
            agma20152B15: [],
            iso132822020: [],
          }
        };
      },
      mapParts() {
        // Get gear parts from cache and map to local state for TOC and tabular display.

        // get gear parts
        const parts = this.$store.getters.getPartsGearDetail();

        // map to local state
        this.gearParts = parts.map(part => this.mapGearDetailPart(part));

        // order the same as feature tree
        sortByString(this.gearParts, 'label');
      },

      // COLLAPSE NAMES
      collapseName(name, item) {
        return name + item.Id;
      },
      collapsePartNames(part) {
        return this.$options.collapseNames.part.map(name => this.collapseName(name, part));
      },
      getCollapsePart(collapseName, name) {
        const partId = collapseName.split(name)[1];
        return getObjectById(this.gearParts, partId);
      },

      // COLLAPSE CLICKS
      changeCollapse(activeNames) {
        // Handle the change of collapse state; queries materials and qualities if expanded.

        let part;
        activeNames.forEach(activeName => {
          if (activeName.startsWith('Material')) {
            part = this.getCollapsePart(activeName, 'Material');
            this.queryMaterials(part).finally(window.MathJax.typeset);
          } else if (activeName.startsWith('Flank')) {
            part = this.getCollapsePart(activeName, 'Flank Quality');
            this.queryFlankQualities(part).finally(window.MathJax.typeset);
          } else if (activeName.startsWith('Radial')) {
            part = this.getCollapsePart(activeName, 'Radial Quality');
            this.queryRadialQualities(part).finally(window.MathJax.typeset);
          }
        })
      },
      collapsePart(part) {
        const removeNames = this.collapsePartNames(part);
        this.activePartSections = this.activePartSections.filter(name => !removeNames.includes(name));
      },
      expandPart(part) {
        this.activePartSections.push(...this.collapsePartNames(part));
        this.changeCollapse(this.activePartSections);
      },
    },
  }
</script>

<style scoped lang="scss" src="./assets/report-content.scss"/>
<style scoped>
    .empty-notes {
        display: grid;
        grid-auto-flow: row;
        margin-left: 1em;
        color: var(--color);
    }
</style>
